import { Inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, NavigationEnd, Router } from "@angular/router";
import { DOCUMENT } from "@angular/common";
import { filter } from "rxjs/operators";
import { EnvironmentConfigsLoaderService } from "./environment-configs-loader.service";

declare global {
  interface Window {
    Recite?: {
      load: (options: Record<string, unknown>) => void;
      enable: () => void;
      disable: () => void;
      isEnabled: () => boolean;
      Event: {
        subscribe: (eventName: string, callback: () => void) => void;
      };
    };
  }
}

/**
 * Loads and toggles the ReciteMe accessibility/translation widget, but only on
 * routes that opt in via `data: { reciteMe: true }` (parent-portal, public-parent-page
 * and public-self-portal areas) - see RoutingModule. Staff-facing areas (screener,
 * admin, manage-user, etc.) never load the widget. Starts closed unless the user
 * has explicitly enabled it via the Accessibility launcher.
 */
@Injectable({
  providedIn: "root",
})
export class ReciteMeService {
  private scriptLoaded = false;
  private scriptLoading = false;
  private launcherEl: HTMLButtonElement | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private environmentConfigsLoaderService: EnvironmentConfigsLoaderService
  ) {}

  public init(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.onNavigationEnd());
  }

  private onNavigationEnd(): void {
    const isReciteMeRoute = this.isReciteMeRoute();

    if (isReciteMeRoute && this.isPersistedEnabled()) {
      this.enableReciteMe();
    } else {
      this.hideReciteMe();
    }

    this.refreshLauncherVisibility(isReciteMeRoute);
  }

  // Only an explicit opt-in opens the toolbar. ReciteMe's close button sets
  // Recite.Persist=false, so both new visitors and users who closed it stay closed.
  private isPersistedEnabled(): boolean {
    return this.getCookie("Recite.Persist") === "true";
  }

  private getCookie(name: string): string | null {
    const prefix = `${name}=`;
    const cookies = this.document.cookie.split(";");

    for (let cookie of cookies) {
      while (cookie.charAt(0) === " ") {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(prefix) === 0) {
        return cookie.substring(prefix.length);
      }
    }

    return null;
  }

  private isReciteMeRoute(): boolean {
    let route: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;

    while (route) {
      if (route.data && route.data.reciteMe === true) {
        return true;
      }
      route = route.firstChild;
    }

    return false;
  }

  // `enable`/`disable`/`isEnabled` are ReciteMe's actual runtime toolbar API
  // (confirmed via window.Recite at runtime) for toggling widget visibility once
  // the script has loaded.
  private enableReciteMe(): void {
    const apiKey = this.environmentConfigsLoaderService.ReciteMeApiKey;

    if (!apiKey) {
      return;
    }

    if (!this.scriptLoaded && !this.scriptLoading) {
      this.loadScript(apiKey);
      return;
    }

    if (window.Recite && typeof window.Recite.enable === "function" && !window.Recite.isEnabled()) {
      window.Recite.enable();
    }
  }

  private hideReciteMe(): void {
    if (window.Recite && typeof window.Recite.disable === "function" && window.Recite.isEnabled()) {
      window.Recite.disable();
    }
  }

  // Shows a small floating "Accessibility" launcher button on ReciteMe-enabled
  // routes so users can open the widget initially or after using its "close" (X)
  // button, which sets Recite.Persist=false and hides the toolbar until re-enabled.
  // Hidden while the ReciteMe toolbar itself is open, since it isn't needed then.
  private refreshLauncherVisibility(isReciteMeRoute: boolean): void {
    if (isReciteMeRoute && !this.isWidgetOpen()) {
      this.createLauncher();
    } else {
      this.removeLauncher();
    }
  }

  private isWidgetOpen(): boolean {
    return !!window.Recite && typeof window.Recite.isEnabled === "function" && window.Recite.isEnabled();
  }

  private createLauncher(): void {
    if (this.launcherEl) {
      return;
    }

    const button = this.document.createElement("button");
    button.type = "button";
    button.id = "recite-me-launcher";
    button.title = "Open accessibility toolbar";
    button.setAttribute("aria-label", "Open accessibility toolbar");
    button.textContent = "Accessibility";
    button.style.cssText = [
      "position: fixed",
      "bottom: 16px",
      "left: 16px",
      "z-index: 2147483000",
      "padding: 10px 14px",
      "background: #002d5b",
      "color: #fff",
      "border: none",
      "border-radius: 24px",
      "font-size: 14px",
      "cursor: pointer",
      "box-shadow: 0 2px 8px rgba(0,0,0,0.3)",
    ].join(";");

    button.addEventListener("click", () => this.reopenReciteMe());

    this.document.body.appendChild(button);
    this.launcherEl = button;
  }

  private removeLauncher(): void {
    if (this.launcherEl) {
      this.launcherEl.remove();
      this.launcherEl = null;
    }
  }

  private reopenReciteMe(): void {
    // Clear ReciteMe's own persisted-disabled cookie so it doesn't immediately
    // re-hide itself on the next navigation/reload, then (re)enable the widget.
    this.document.cookie = "Recite.Persist=true; path=/";
    this.removeLauncher();
    this.enableReciteMe();
  }

  private loadScript(apiKey: string): void {
    this.scriptLoading = true;

    const script = this.document.createElement("script");
    script.type = "text/javascript";
    script.src = `https://api.reciteme.com/asset/js?key=${apiKey}`;
    script.async = true;
    script.onload = () => {
      this.scriptLoaded = true;
      this.scriptLoading = false;

      if (window.Recite) {
        // Mirrors the proven working integration: Recite.load() bootstraps/renders
        // the toolbar UI; enable() alone (without load()) does not build the UI.
        window.Recite.Event.subscribe("Recite:load", () => {
          window.Recite.enable();
        });
        // Per ReciteMe support: hide Gaelic (Irish "ga" / Scottish Gaelic "gd")
        // from the translation language list once the toolbar controls render.
        window.Recite.Event.subscribe("Controls:loaded", () => {
          this.document.querySelectorAll('[data-language="ga"],[data-language="gd"]').forEach((e) => e.remove());
        });
        window.Recite.load({});
      }
    };
    script.onerror = () => {
      this.scriptLoading = false;
      console.error("ReciteMe: failed to load widget script (check network/CSP).");
    };

    this.document.getElementsByTagName("head")[0].appendChild(script);
  }
}
